import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  OnInit,
  Renderer2,
} from '@angular/core';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { tap, takeUntil, take, map, catchError, switchMap } from 'rxjs/operators';

import { SessionWarningModalComponent } from '@global/shared-features/modals/session-warning-modal/session-warning-modal.component';

import { SessionService } from '@global/services/session/session.service';
import { ModalService } from 'dlg-walrus';

import { MainActions, RenewActions } from '@ren/main/state/actions';

import {
  selectJourneyPath,
  selectNavigationDetails,
  selectPanelConfig,
} from '@ren/main/state/selectors/journey.selectors';
import {
  selectIsAutoRenewal,
  selectOnlineNegotiationStatus,
  selectPolicyChangeDate,
  selectPolicyId,
  selectProgressbarDisplayStatus,
  selectShowRenewDetails,
} from '@ren/main/state/selectors/renewal.selectors';

import { ButtonState } from '@global/infrastructure/interfaces/button-state';
import { PanelState } from '@global/infrastructure/interfaces/panel-state';
import { environment } from '@ren-env/environment';
import { SESSION_TIMEOUT_URL } from '@ren/infrastructure/constants';
import { JourneyFeaturesConfig } from '@ren/infrastructure/configs/journey-features.config';

import { PostNavigationService } from '@global/services/post-navigation/post-navigation.service';
import { Utility } from '@global/infrastructure/helpers/utility';
import { selectExistingPremium, selectDiscountApplied, selectLatestPremium } from '../state/selectors/policy.selectors';
import { ContainerBase } from '@global/infrastructure/abstracts/container/container.base';
import { selectYourAdditionalDriversSummary, selectYourCarSummary, selectYourCoverSummary, selectYourDetailsSummary, selectYourDiscountsSummary, selectYourDrivingHistorySummary, selectYourPaymentsSummary, selectYourQuoteSummary } from '@ren/features/review/state/selectors/review.selectors';
import { AnalyticsService } from '@global/services/analytics/analytics.service';
import { Brand } from '@global/infrastructure/enums/brand-type.enum';

@Component({
  selector: 'app-main-container',
  templateUrl: './main-container.component.html',
})
export class MainContainerComponent
  extends ContainerBase
  implements OnInit, AfterViewChecked
{
  navigationDetails$: Observable<ButtonState[]>;
  panelConfig$: Observable<PanelState>;
  isShowProgressBar$: Observable<boolean>;
  isShowJourneyNav$: Observable<boolean>;
  policyId$: Observable<string>;
  policyChangeDate$: Observable<Date>;
  journeyPath$: Observable<any>;
  isReviewSelected$: Observable<boolean>;
  policyId: string;
  isInitialPage: boolean;
  isEditPage: boolean;

  brandName = environment.brand_copy.brand;
  cookieUrl = environment.brand_copy.cookieUrl;
  isAutoRenewal$: Observable<boolean>;
  isDlBrand = environment.brand_copy.brandSimple === Brand.DirectLine;

  constructor(
    private readonly store: Store,
    private readonly cd: ChangeDetectorRef,
    private readonly title: Title,
    private readonly router: Router,
    private readonly sessionService: SessionService,
    private readonly modalService: ModalService,
    private readonly postNavigationService: PostNavigationService,
    private readonly analyticsService: AnalyticsService,
    private readonly renderer: Renderer2
  ) {
    super();
    postNavigationService.renderer = this.renderer;
  }

  ngOnInit(): void {
    this.store.dispatch(
      RenewActions.showProgressBar({ isDisplayProgressBar: false })
    );
    this.navigationDetails$ = this.store.pipe(select(selectNavigationDetails));
    this.isReviewSelected$ = this.store.pipe(select(selectShowRenewDetails));
    this.panelConfig$ = this.store.pipe(
      select(selectPanelConfig),
      tap((config) => {
        if (config.pageTitle) {
          this.title.setTitle(config.pageTitle);
        }
      })
    );
    this.policyId$ = this.store.pipe(
      select(selectPolicyId),
      tap((policyId) => {
        this.policyId = policyId;
      })
    );
    this.policyChangeDate$ = this.store.pipe(select(selectPolicyChangeDate));
    this.journeyPath$ = this.store.pipe(select(selectJourneyPath));

    this.isShowProgressBar$ = this.store.pipe(
      select(selectProgressbarDisplayStatus)
    );

    this.postNavigationService
      .onNavigate()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((url) => this.setShowJourneyNav(url)),
        switchMap((url) => this.setupAnalytics(url))
      )
      .subscribe((data) => {
        this.trackQuoteData(data);
      });

    this.sessionService.getSessionExpirySeconds$.subscribe((session) =>
      this.openSessionExpiryModal(session)
    );
  }

  ngAfterViewChecked(): void {
    this.cd.detectChanges();
  }

  private openSessionExpiryModal(session) {
    if (
      this.router.url !==
      `${JourneyFeaturesConfig.errors.path}/${SESSION_TIMEOUT_URL}`
    ) {
      const expirySeconds = session.secondsRemaining;
      if (expirySeconds > 0) {
        this.modalService
          .open(SessionWarningModalComponent, { expirySeconds })
          .closed$.subscribe((result) => {
            if (result === 'exit') {
              this.store.dispatch(MainActions.redirectToMyAccountDashboard());
            } else if (result === 'timeout') {
              this.router.navigate([
                `${JourneyFeaturesConfig.errors.path}/${SESSION_TIMEOUT_URL}`,
              ]);
            } else {
              this.sessionService
                .refreshSession(session.sessionId)
                .pipe(
                  take(1),
                  catchError(() =>
                    this.router.navigate([
                      `${JourneyFeaturesConfig.errors.path}/${SESSION_TIMEOUT_URL}`,
                    ])
                  )
                )
                .subscribe(() => this.ngOnInit());
            }
          });
      } else {
        this.router.navigate([
          `${JourneyFeaturesConfig.errors.path}/${SESSION_TIMEOUT_URL}`,
        ]);
      }
    }
  }

  private setShowJourneyNav(url: string) {
    this.isAutoRenewal$ = this.store.pipe(select(selectIsAutoRenewal));
    this.isShowJourneyNav$ = combineLatest([
      this.isAutoRenewal$,
      this.store.pipe(select(selectOnlineNegotiationStatus)),
      this.store.pipe(select(selectDiscountApplied)),
      Utility.isAbTest('ds1-5711'),
      Utility.isAbTest('ds1-5598'),
    ]).pipe(
      map(([isAuto, negotiation, discountApplied, abTestMan, abTestAuto]) => {
        if (
          discountApplied !== true &&
          ((abTestMan && !isAuto) || (abTestAuto && isAuto)) &&
          url.indexOf(JourneyFeaturesConfig.reviewInitial.path) > -1
        ) {
          return false;
        }
        const isAutoOrInitial =
          isAuto ||
          url.indexOf(JourneyFeaturesConfig.reviewInitial.path) === -1;
        return (
          isAutoOrInitial &&
          !(url.indexOf(JourneyFeaturesConfig.premium.path) > -1 && negotiation)
        );
      })
    );
    this.isInitialPage = window.location.href.includes(
      JourneyFeaturesConfig.reviewInitial.path
    );
    this.isEditPage = window.location.href.includes(
      JourneyFeaturesConfig.reviewEdit.path
    );
    return of(url);
  }

  private setupAnalytics(url:string) {
    if (url.indexOf('/load/') > -1) return EMPTY;
    return combineLatest([
      this.store.pipe(select(selectYourQuoteSummary)),
      this.store.pipe(select(selectYourCarSummary)),
      this.store.pipe(select(selectYourDetailsSummary)),
      this.store.pipe(select(selectYourAdditionalDriversSummary)),
      this.store.pipe(select(selectYourCoverSummary)),
      this.store.pipe(select(selectYourDrivingHistorySummary)),
      this.store.pipe(select(selectYourDiscountsSummary)),
      this.store.pipe(select(selectYourPaymentsSummary)),
      this.store.pipe(select(selectLatestPremium)),
      this.store.pipe(select(selectIsAutoRenewal)),
      this.store.pipe(select(selectExistingPremium)),
    ]).pipe(
      map(
        ([
          yourQuoteSummary,
          yourCarSummary,
          yourDetailsSummary,
          yourAdditionalDriversSummary,
          yourCoverSummary,
          yourDrivingHistorySummary,
          yourDiscountSummary,
          yourPaymentsSummary,
          premium,
          autoRenewal,
          existingPremium,
        ]) => ({
          yourQuoteSummary,
          yourCarSummary,
          yourDetailsSummary,
          yourAdditionalDriversSummary,
          yourCoverSummary,
          yourDrivingHistorySummary,
          yourDiscountSummary,
          yourPaymentsSummary,
          premium,
          autoRenewal,
          existingPremium,
        })
      ),
      takeUntil(this.destroy$)
    );
  }

  private trackQuoteData(data) {
    if (data?.premium?.bindData) {
      const apr =
        data.premium.bindData?.offeredPaymentPlans_dlg[0]?.paymentPlans.filter(
          (pp) => pp.name.toLowerCase().indexOf('monthly') > -1
        )[0];

      const quotePrice =
        data.premium?.quoteData?.offeredQuotes?.find(
          (qt) => qt?.publicID === data.premium?.selectedPeriodPublicId_dlg
        )?.premium?.total?.amount ?? data.yourQuoteSummary?.quotePrice;

      data.premium.quoteData.offeredQuotes.forEach((qt) => {
        if (qt.branchCode === data.yourCoverSummary?.coverLevelSimple) {
          this.analyticsService.trackPolicy({
            name: data.premium.baseData.productName,
            price: qt.premium.total.amount,
            last_year_price: data.existingPremium,
            policyNumber: data.premium.policyNumber,
            cover_level: qt.branchCode,
            ...this.analyticsService.formatAddons(
              data.premium.lobData.mOTLine_Ext.offerings
            ),
            policy_type:
              data.premium?.baseData?.channel_dlg === 'pcw' && this.isDlBrand
                ? 'Online Only'
                : 'Standard',
          });
        }
      });

      const sourceSite =
        data.premium?.baseData?.source_dlg !== 'directdigital'
          ? data.premium?.baseData?.source_dlg
          : '';
      this.analyticsService.trackSource(sourceSite);
      this.analyticsService.trackFeature('quote', {
        renew: data.autoRenewal ? 'Yes' : 'No',
        price: quotePrice,
        apr: apr ? apr.aPR_dlg : 0,
        payment_type: data.yourPaymentsSummary?.paymentType,
        card_type: data.yourPaymentsSummary?.cardType,
        quoteID: data.premium.quoteID,
        existing_policy_renewal_date: data.yourCoverSummary?.policyStartDate,
      });
    }

    this.analyticsService.trackFeature('yourCarPerm', data.yourCarSummary);

    const accountHolderDOB = data.yourDetailsSummary?.dateOfBirth.split('/');
    let users = [];
    const user = {
      dob: accountHolderDOB,
      sso_id: data.premium.baseData.accountHolder.oktaid_dlg,
      postCode: data.premium.baseData.accountHolder.primaryAddress.postalCode,
      emailAddress: data.premium.baseData.accountHolder.emailAddress1,
      title: data.premium.baseData.accountHolder.prefix,
      gender: data.premium.baseData.accountHolder.gender,
      employmentStatus:
        data.premium.baseData.accountHolder.employmentStatus_dlg,
      maritalStatus: data.premium.baseData.accountHolder.maritalStatus,
      userType: data.premium.baseData.accountHolder.accountHolder
        ? 'main'
        : 'additional',
      occupation: data.yourDetailsSummary?.occupation,
      claims: data.yourDrivingHistorySummary?.claims,
      convictions: data.yourDrivingHistorySummary?.convictions,
      licenseHeldFor: data.yourDrivingHistorySummary?.licenseHeldFor,
      licenseType: data.yourDrivingHistorySummary?.licenseType,
      ukResident:
        data.yourDrivingHistorySummary?.ukResidentSince === 'Birth'
          ? 'Yes'
          : 'No',
      ukResidentSince: data.yourDrivingHistorySummary?.ukResidentSince,
      policy: [
        {
          renewal_date: data.yourCoverSummary?.policyStartDate,
        },
      ],
      ...data.yourDetailsSummary,
      ...data.yourDrivingHistorySummary,
      ...data.yourDiscountSummary,
    };

    users.push(user);

    if (
      data.yourAdditionalDriversSummary &&
      data.yourAdditionalDriversSummary.length > 0
    ) {
      const additionalDrivers = data.yourAdditionalDriversSummary.map((d) => {
        const driver = {
          dob: d?.dob ? d.dob.split('/') : null,
          ukResident: d.ukResidentSince === 'Birth' ? 'Yes' : 'No',
          ukResidentSince: d.ukResidentSince,
          userType: 'additional',
        };
        return {
          ...d,
          ...driver,
        };
      });
      if (additionalDrivers.length > 0) {
        users = [...users, ...additionalDrivers];
      }
    }
    this.analyticsService.trackFeature('yourDetails', users);
    window.utag?.view();
  }
}
